import React, { Suspense } from "react";
import { FormattedMessage, WrappedComponentProps } from "react-intl";
import { RootStoreProp } from "../../../store";
import { getCubaREST, MainStoreInjected } from "@cuba-platform/react";
import { RouteComponentProps } from "react-router-dom";
import { FormComponentProps } from "antd/lib/form";
import { IReactionDisposer, isArrayLike, observable, reaction } from "mobx";
import { ProcessInstanceData } from "../../../../cuba/entities/base/bproc_ProcessInstanceData";
import { ExtTaskData } from "../../../../cuba/entities/base/tsadv_ExtTaskData";
import { BprocFormData } from "../../../../cuba/entities/bproc/bproc_FormData";
import { restServices } from "../../../../cuba/services";
import { DataInstanceStore } from "@cuba-platform/react/dist/data/Instance";
import Button, { ButtonType } from "../../../components/Button/Button";
import { AbstractBprocRequest } from "../../../../cuba/entities/base/AbstractBprocRequest";
import Notification from "../../../util/Notification/Notification";
import moment from "moment/moment";
import { TsadvUser } from "../../../../cuba/entities/base/tsadv$UserExt";
import {
  parseToFieldValueFromDataInstanceValue,
  parseToJsonFromFieldValue,
} from "../../../components/MultiFileUpload";
import { goBackOrHomePage, setItemPropertiesValue } from "../../../util/util";
import { CustomPropsBproc } from "../modal/StartBprocModal";
import { CenteredLoader } from "../../../CenteredLoader";
import { MetaPropertyInfo } from "@cuba-platform/rest";

const ExtTaskDataCards = React.lazy(
  () => import("../TaskData/ExtTaskDataCards"),
);
const BprocButtons = React.lazy(
  () => import("../buttons/BprocButtons"),
);

type Props = FormComponentProps & EditorProps;

type EditorProps = {
  entityId: string;
};

/**
 * Абстракный редактор сущности для БПМ заявок <br>
 * Пример: {@link CertificateRequestEditComponent}
 *
 * T - тип сущност, K - props
 */
abstract class AbstractBprocEdit<
  T extends AbstractBprocRequest,
  K
> extends React.Component<
  K &
  Props &
  WrappedComponentProps &
  RootStoreProp &
  MainStoreInjected &
  RouteComponentProps<any>
> {
  /**
   * Ссылка на дата инстанс создавемого/редактируемого заявок, обязательно нужно переопределить
   */
  dataInstance: DataInstanceStore<T>;

  @observable
  updated = false;

  reactionDisposer: IReactionDisposer;

  @observable
  mainStore = this.props.mainStore!;

  /**
   * Сотрудник по которому запускается процесс
   */
  @observable
  employee: TsadvUser;

  @observable
  employeePersonGroupId: string;

  /**
   * Ссылка на процесс инстанс, уникальный инстанс для каждого процесса. Если процесс не начато то значение null
   */
  processInstanceData: ProcessInstanceData | null;

  isCalledProcessInstanceData = false;

  /**
   * Ссылка на процессой задачи
   */
  @observable
  tasks: ExtTaskData[] | null;


  customKeyForTaskService: string | null;
  /**
   * Ссылка на активной процессой задачи, где утверждающий текущий пользователь
   */
  @observable
  activeUserTask: ExtTaskData | null;

  /**
   * Ссылка на активной процессой задачи
   */
  @observable
  activeTask: ExtTaskData | null;

  @observable
  activeTaskAdditional: ExtTaskData | null;

  @observable
  formData: BprocFormData | null;

  @observable
  isStartForm: boolean;

  @observable
  globalErrors: string[] = [];

  createElement = React.createElement;

  @observable
  isValidatedSuccess = false;

  @observable
  isUserInitiator = false;

  @observable isOpened = false;
  @observable isNeedUpdateOnRevision = false;
  @observable openedValueId: string | null = "UNREJECT";
  /**
   * Список поле для валидаций и сохранение заявок, обязательно нужно переопределить
   */
  fields: string[];

  /**
   * Уникальный код процесса, обязательно нужно переопределить
   */
  processDefinitionKey: string;

  path: string;

  /**
   * Список ответов который требует комментарий
   */
  commentRequiredOutcomes = ["REJECT", "REVISION", "ON_HOLD"];

  validationRequiredOutcomes = ["START", "APPROVE", "UNREJECT", "SEND_FOR_APPROVAL"];

  isStartCommentVisible = false;

  /**
   * Если true то перед ответом сохраняет запись
   */
  isUpdateBeforeOutcome = false;

  /**
   * Если true то перед откыртыием модального окна сохраняет запись
   */
  isNeedUpdateEntitiesBeforeOpenModal = false;

  logPerf(label: string, start?: number) {
    const now = performance.now();
    if (start) {
      console.log(`⏱ [${label}] ${(now - start).toFixed(2)}ms`);
    } else {
      console.log(`▶️ [${label}] start at ${now.toFixed(2)}ms`);
    }
    return now;
  }

  validate = (): Promise<boolean> => {
    let isValidatedSuccess = true;
    this.props.form.validateFields(
      this.fields,
      { force: true },
      (err, values) => {
        isValidatedSuccess = !err;
        if (err) {
          Notification.error({
            message: this.props.intl.formatMessage({
              id: "management.editor.validationError",
            }),
          });
        }
      },
    );
    return new Promise(resolve => resolve(isValidatedSuccess));
  };

  getUpdateEntityData(): any {
    const obj = {
      ...this.props.form.getFieldsValue(this.fields),
    };

    const metaClass = this.props.mainStore!.metadata!.find(
      mci => mci.entityName === this.dataInstance.entityName,
    );
    if (metaClass) {
      metaClass.properties
        .filter(value => value.type === "sys$FileDescriptor")
        .filter(
          value =>
            value.cardinality === "ONE_TO_MANY" ||
            value.cardinality === "MANY_TO_MANY",
        )
        .filter(value => this.fields.find(field => field === value.name))
        .forEach(value => {
          const files = obj[value.name];
          if (files) obj[value.name] = parseToJsonFromFieldValue(files);
        });
    }

    return obj;
  }

  updateItemValue = (): void => {
    this.dataInstance.setItem(
      setItemPropertiesValue(
        this.props.mainStore!,
        this.dataInstance.entityName,
        this.dataInstance.item,
        this.getUpdateEntityData(),
      ),
    );
  };

  updateAndCommit = (): Promise<void> => {
    this.updateItemValue();
    return this.dataInstance.commit();
  };

  setOpenedOutcomeId = (value: string | null) => {
    this.openedValueId = value;
  };

  saveRequest = (e: React.MouseEvent, options: { afterCommit?: () => Promise<void> } = {}) => {
    e.preventDefault();

    const { afterCommit } = options;

    this.props.form.validateFields(
      this.fields,
      { force: true },
      (err, values) => {
        if (err) {
          Notification.error({
            message: this.props.intl.formatMessage({
              id: "management.editor.validationError",
            }),
          });
          return;
        }
        this.updateAndCommit().then(afterCommit).then(value =>
          this.props.history.push(this.path + "/" + this.dataInstance.item!.id),
        ).catch(err => {
        });
      },
    );
  };

  /**
   * return Таблица из списка процессных задач
   */
  takCard = () => {
    if (!this.tasks) return <div />;
    const tasks = Array.from(this.tasks);
    return (
      <Suspense fallback={<CenteredLoader />}>
        <ExtTaskDataCards isDep={true} tasks={tasks} />
      </Suspense>

    );
  };

  takeCardDep = () => {
    if (!this.tasks) return <div />;
    const tasks = Array.from(this.tasks);
    return (
      <Suspense fallback={<CenteredLoader />}>
        <ExtTaskDataCards isDep={true} tasks={tasks} />
      </Suspense>
    );
  };

  isNotDraft = () => {
    const status = this.getStatusCode();
    return (
      status !== "DRAFT" && status !== "TO_BE_REVISED" && status !== undefined
    );
  };

  getStatusCode = () => {
    return (
      this.dataInstance.item &&
      this.dataInstance.item.status &&
      this.dataInstance.item.status.code
    );
  };

  isOpenedModal = (value: boolean) => {
    this.isOpened = value;
  };

  isNotDraftVRE = () => {
    const status = this.getStatusCode();
    return (
      status !== "DRAFT" && status !== "TO_BE_REVISED" && status !== undefined
    );
  };

  isOnApproving = () => {
    return this.dataInstance.item && this.dataInstance.item.status
      ? this.dataInstance.item.status.code === "APPROVING"
      : false;
  };

  isOnHold = () => {
    return this.dataInstance.item && this.dataInstance.item.status
      ? this.dataInstance.item.status.code === "ON_HOLD"
      : false;
  };

  isOnRevision = () => {
    return this.dataInstance.item && this.dataInstance.item.status
      ? this.dataInstance.item.status.code === "TO_BE_REVISED"
      : false;
  };

  setEmployee = (personGroupId: string): Promise<TsadvUser> => {
    this.employeePersonGroupId = personGroupId;
    return getCubaREST()!
      .searchEntities<TsadvUser>(
        TsadvUser.NAME,
        {
          conditions: [
            {
              property: "personGroup.id",
              operator: "=",
              value: personGroupId,
            },
            {
              property: "active",
              operator: "=",
              value: "TRUE",
            },
          ],
        },
        {
          view: "portal-bproc-users",
        },
      )
      .then(value => (this.employee = value[0]));
  };

  beforeCompletePredicate = (outcome: string): Promise<boolean> =>
    new Promise(resolve => resolve(true));

  /**
   * return Список доступных кнопок для процесса
   */

  excludeOutcomes: string[] | null = null;

  getOutcomeBtns = (
    isNeedBpm?: any,
    recruiterEnable?: boolean,
    callback?: (id?: string) => Promise<void>,
    customProps?: CustomPropsBproc,
  ): JSX.Element | null => {
    const { status } = this.dataInstance;
    if (isNeedBpm !== false) isNeedBpm = true;

    return (
      <Suspense fallback={<CenteredLoader />}>
        {this.formData ? (
          isNeedBpm ? (
            <BprocButtons
              customProps={customProps}
              dataInstance={this.dataInstance}
              recruiterEnable={recruiterEnable}
              formData={this.formData}
              setOpenedOutcomeId={this.setOpenedOutcomeId}
              validate={this.validate}
              beforeCompletePredicate={this.beforeCompletePredicate}
              needValidationsOutcomes={this.validationRequiredOutcomes}
              employeePersonGroupId={() =>
                this.employeePersonGroupId
                  ? this.employeePersonGroupId
                  : this.props.rootStore!.userInfo.personGroupId!
              }
              update={this.updateAndCommit}
              processInstanceData={this.processInstanceData}
              afterSendOnApprove={this.afterSendOnApprove}
              callback={callback}
              isStartForm={this.isStartForm}
              processDefinitionKey={this.processDefinitionKey}
              form={this.props.form}
              isUpdateBeforeOutcome={this.isUpdateBeforeOutcome}
              commentRequiredOutcomes={this.commentRequiredOutcomes}
              isStartCommentVisible={this.isStartCommentVisible}
              isUserInitiator={this.isUserInitiator}
              getRequest={() => this.getRequestWithUpdate()}
              task={this.activeTask}
              tasks={this.tasks}
              isNeedUpdateEntitiesBeforeOpenModal={this.isNeedUpdateEntitiesBeforeOpenModal}
              loadBpmProcessData={this.loadBpmProcessData}
            />
          ) : (
            <Button
              buttonType={ButtonType.PRIMARY}
              onClick={() => {
                this.validate().then(isValid => {
                  if (isValid) {
                    this.updateAndCommit()
                      .then(() => (this.updated = true))
                      .catch((e: any) => {
                        Notification.error({
                          message: this.props.intl.formatMessage({
                            id: "management.editor.error",
                          }),
                        });
                      });
                  }
                });
              }}
              disabled={status !== "DONE" && status !== "ERROR"}
              loading={status === "LOADING"}
              style={{ marginLeft: "8px" }}
            >
              <FormattedMessage id="management.editor.submit" />
            </Button>
          )
        ) : null}
      </Suspense>
    );

  };
  getRequest = () => {
    return this.dataInstance.item as AbstractBprocRequest;
  };

  getRequestWithUpdate = () => {
    this.updateItemValue();
    return this.dataInstance.item as AbstractBprocRequest;
  };
  getOutcomeBtnsWccf = (
    isNeedBpm?: any,
    recruiterEnable?: boolean,
  ): JSX.Element | null => {
    const { status } = this.dataInstance;

    if (isNeedBpm !== false) isNeedBpm = true;

    return this.formData ? (
      isNeedBpm ? (
        <BprocButtons
          dataInstance={this.dataInstance}
          recruiterEnable={recruiterEnable}
          formData={this.formData}
          validate={this.validate}
          beforeCompletePredicate={this.beforeCompletePredicate}
          employeePersonGroupId={() =>
            this.employeePersonGroupId
              ? this.employeePersonGroupId
              : this.props.rootStore!.userInfo.personGroupId!
          }
          update={this.updateAndCommit}
          setOpenedOutcomeId={this.setOpenedOutcomeId}
          processInstanceData={this.processInstanceData}
          afterSendOnApprove={this.afterSendOnApprove}
          needValidationsOutcomes={this.validationRequiredOutcomes}
          isStartForm={this.isStartForm}
          processDefinitionKey={this.processDefinitionKey}
          form={this.props.form}
          isUpdateBeforeOutcome={this.isUpdateBeforeOutcome}
          commentRequiredOutcomes={this.commentRequiredOutcomes}
          isStartCommentVisible={this.isStartCommentVisible}
          isUserInitiator={this.isUserInitiator}
          getRequest={() => this.getRequest()}
          isNeedUpdateEntitiesBeforeOpenModal={
            this.isNeedUpdateEntitiesBeforeOpenModal
          }
          task={this.activeTask}
          tasks={this.tasks}
          loadBpmProcessData={this.loadBpmProcessData}
        />
      ) : (
        <Button
          buttonType={ButtonType.PRIMARY}
          onClick={() => {
            this.validate().then(isValid => {
              if (isValid) {
                this.updateAndCommit()
                  .then(() => (this.updated = true))
                  .catch((e: any) => {
                    Notification.error({
                      message: this.props.intl.formatMessage({
                        id: "management.editor.error",
                      }),
                    });
                  });
              }
            });
          }}
          disabled={status !== "DONE" && status !== "ERROR"}
          loading={status === "LOADING"}
          style={{ marginLeft: "8px" }}
        >
          <FormattedMessage id="management.editor.submit" />
        </Button>
      )
    ) : null;
  };

  componentDidMount() {
    this.setReactionDisposer();
    this.loadData();
    this.loadBpmProcessData();
  }

  processInstanceBusinessKey = (): string => {
    return this.props.entityId;
  };

  initVariablesByBproc = () => {
  };

  loadData = async () => {
    const start = this.logPerf("loadData");
    if (!this.isNewEntity()) {
      await this.dataInstance.load(this.props.entityId);
    } else {
      const entityName = this.dataInstance.entityName;
      this.initItem(
        await restServices.portalHelperService.newEntity({
          entityName: entityName,
        }),
      );

      this.logPerf("loadData", start);
    }
  };

  findMyActiveTask = (tasks: ExtTaskData[] | undefined, userId: string | undefined): ExtTaskData | undefined => {
    if (!tasks || !userId) return undefined;

    for (const task of tasks) {
      if (task.assignee === userId && !task.endTime) {
        return task;
      }

      if (task.childs && task.childs.length > 0) {
        const result = this.findMyActiveTask(task.childs, userId);
        if (result) return result;
      }
    }

    return undefined;
  };

  loadBpmProcessData = () => {
    const processDefinitionKey = this.processDefinitionKey;
    if (this.props.entityId !== "new") {
      restServices.bprocService
        .processInstanceData({
          processInstanceBusinessKey: this.processInstanceBusinessKey(),
          processDefinitionKey: processDefinitionKey,
        })
        .then(value => {
          this.isCalledProcessInstanceData = true;
          this.processInstanceData = value;
          if (value) {
            this.isUserInitiator =
              value.startUserId === this.props.rootStore!.userInfo!.id;
            const serviceKey = this.customKeyForTaskService || "bprocService";

            const service = restServices[serviceKey as keyof typeof restServices] as any;
            service
              .tasks({ processInstanceData: value })
              .then(tasks => {
                this.tasks = tasks;
                this.activeTask = tasks.find(
                  task => !task.endTime,
                ) as ExtTaskData;


                const additionalTask = this.findMyActiveTask(this.activeTask?.childs, this.props.rootStore?.userInfo?.id);
                this.activeTaskAdditional = additionalTask;
                this.activeUserTask =
                  this.activeTask &&
                  this.activeTask.assigneeOrCandidates &&
                  this.activeTask.assigneeOrCandidates.some(
                    user => user.id === this.props.rootStore!.userInfo.id,
                  )
                    ? this.activeTask
                    : null;
                if (this.activeTaskAdditional && this.activeTaskAdditional.outcome !== "TASK_CLOSED") {
                  restServices.bprocFormService
                    .getTaskFormData({ taskId: value.id })
                    .then(formData => {
                      this.formData = this.excludeOutcomes
                        ? {
                          ...formData,
                          outcomes: formData.outcomes!.filter(outcome => {
                            return !this.excludeOutcomes!.includes(
                              outcome!.id!,
                            );
                          }),
                        }
                        : formData;
                      this.isStartForm = false;
                      this.initVariablesByBproc();
                    });
                }
                if (this.activeUserTask)
                  restServices.bprocFormService
                    .getTaskFormData({ taskId: this.activeUserTask.id! })
                    .then(formData => {
                      this.formData = this.excludeOutcomes
                        ? {
                          ...formData,
                          outcomes: formData.outcomes!.filter(outcome => {
                            return !this.excludeOutcomes!.includes(
                              outcome!.id!,
                            );
                          }),
                        }
                        : formData;
                      this.isStartForm = false;
                      this.initVariablesByBproc();
                    });
                else {
                  if (this.isUserInitiator && this.activeTask) {
                    this.formData = this.excludeOutcomes ? (
                      this.excludeOutcomes!.includes("CANCEL") ? {
                        outcomes: [],
                      } : {
                        outcomes: [{ id: "CANCEL" }],
                      }) : {
                      outcomes: [{ id: "CANCEL" }],
                    };
                    this.isStartForm = false;
                  }
                  this.initVariablesByBproc();
                }
              });
          } else {
            restServices.bprocService
              .getStartFormData({ processDefinitionKey: processDefinitionKey })
              .then(formData => {
                this.formData = formData;
                this.isStartForm = true;
                this.initVariablesByBproc();
              });
          }
        });
    } else {
      const processDefinitionKey = this.processDefinitionKey;

      restServices.bprocService
        .getStartFormData({ processDefinitionKey: processDefinitionKey })
        .then(formData => {
          this.formData = formData;
          this.isStartForm = true;
          this.initVariablesByBproc();
        });
    }
  };

  protected initItem(request: T): void {
    const start = this.logPerf("initItem:start");

    const t1 = this.logPerf("initItem:dataInstance.setItem:start");
    this.dataInstance.setItem(request);
    this.logPerf("initItem:dataInstance.setItem:end", t1);

    const t2 = this.logPerf("initItem:fastGetFieldValues:start");
    const fieldValues = this.fastGetFieldValues(this.dataInstance.item, this.fields);
    this.logPerf("initItem:fastGetFieldValues:end", t2);

    const t3 = this.logPerf("initItem:form.setFieldsValue:start");
    this.props.form.setFieldsValue(fieldValues);
    this.logPerf("initItem:form.setFieldsValue:end", t3);

    this.logPerf("initItem:end", start);
  }

  setReactionDisposer = () => {
    const start = this.logPerf("setReactionDisposer");
    if (this.reactionDisposer) {
      this.reactionDisposer();
    }
    this.reactionDisposer = reaction(
      () => {
        const item = this.dataInstance.item;
        return item;
      },
      (item) => {
        if (!item || typeof item !== "object") {
          return;
        }

        try {
          if (this.onReactionDisposerEffect) {
            this.onReactionDisposerEffect(item);
          }

          const fieldsValue = this.onReactionFieldsValue(item) || {};

          if (fieldsValue && typeof fieldsValue === "object") {
            if (this.props.form) {
              console.log("🧾 before setFieldsValue", fieldsValue.declaratoryFile);
              this.props.form.setFieldsValue(fieldsValue);
              console.log("🧾 after setFieldsValue", this.props.form.getFieldValue("declaratoryFile"));
            }
          } else {
            console.warn("[reaction trigger] onReactionFieldsValue returned invalid data");
          }
        } catch (e) {
          console.error("[reaction trigger] error:", e);
        }
      },
      {
        delay: 100,
        fireImmediately: true,
      },
    );
    this.logPerf("setReactionDisposer", start);
  };

  fastGetFieldValues(item: any, fields: string[]) {
    const result: Record<string, any> = {};

    const properties = this.props.mainStore?.metadata?.find(
      mci => mci?.entityName === this?.dataInstance?.entityName,
    )?.properties;

    fields.forEach(field => {
      const value = item?.[field];

      const type = properties?.find(item => item?.name === field)?.type || "";
      const isDate = type === "date" || type === "dateTime";

      if (isDate && typeof value === "string") {
        const date = moment(value, moment.ISO_8601, true);
        if (date.isValid()) {
          result[field] = date;
          return;
        }
      }
      // Объекты с id или _instanceName
      if (value && typeof value === "object") {
        result[field] = value.id ?? value._instanceName ?? value;
      } else {
        result[field] = value;
      }
    });

    return result;
  }

  onReactionFieldsValue = (item: T | undefined) => {
    const start = this.logPerf("onReactionFieldsValue");
    const metaClass = this.props.mainStore?.metadata?.find(
      mci => mci?.entityName === this?.dataInstance?.entityName,
    );
    const obj = this.fastGetFieldValues(this.dataInstance.item, this.fields);

    if (this.isCalledProcessInstanceData && !this.processInstanceData) {
      const now = moment().locale(this.props.rootStore!.userInfo.locale!);
      obj["requestDate"] = now;
    }

    if (item) {
      if (metaClass) {
        const fieldsSet = new Set(this.fields);

        const fileProps = metaClass.properties.filter(value =>
          value.type === "sys$FileDescriptor" &&
          (
            value.cardinality === "ONE_TO_MANY" ||
            value.cardinality === "MANY_TO_MANY" ||
            value.cardinality === "MANY_TO_ONE" ||
            value.cardinality === "ONE_TO_ONE"
          ) &&
          fieldsSet.has(value.name),
        );
        fileProps.forEach(value => {
          const files = item[value.name];

          if (files) {
            if (isArrayLike(files)) {
              obj[value.name] = parseToFieldValueFromDataInstanceValue(files);
            } else {
              obj[value.name] = parseToFieldValueFromDataInstanceValue([files]);
            }
          }
        });
        console.groupEnd();
      }
    }

    this.logPerf("onReactionFieldsValue", start);
    console.log("✅ RESULT obj", obj);

    return obj;
  };

  isNewEntity = () => {
    return this.props.entityId === "new";
  };

  onReactionDisposerEffect = (item: T | undefined) => {
  };

  afterSendOnApprove = () => {
    goBackOrHomePage(this.props.history!);
  };

  componentWillUnmount() {
    this.reactionDisposer && this.reactionDisposer();
  }
}

export default AbstractBprocEdit;
